import React, {
  forwardRef,
  useState,
  useEffect,
  useContext,
  ComponentPropsWithRef,
} from "react";
import PropTypes from "prop-types";
import cn from "classnames";

import { ReactDefaultPropsWithChildren } from "@/types/react";

import styles from "./Accordion.scss";

import { AccordionHeadline } from "./AccordionHeadline";
import { AccordionSubline } from "./AccordionSubline";
import { AccordionPanel } from "./AccordionPanel";
import { AccordionToggle } from "./AccordionToggle";

import { AccordionContext } from "./AccordionContext";
import { AccordionItemProvider } from "./AccordionItemContext";

type AccordionItemBaseProps = {
  isExpanded?: boolean;
  itemId?: string;
  customClass?: string;
  onToggle?: (expanded: boolean) => void;
};
export type AccordionItemProps = AccordionItemBaseProps &
  Omit<React.ComponentPropsWithRef<"div">, keyof AccordionItemBaseProps>;

const propTypes = {
  isExpanded: PropTypes.bool,
  itemId: PropTypes.string,
  onToggle: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

const defaultProps = {
  children: "",
  isExpanded: false,
  itemId: undefined,
  customClass: undefined,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onToggle: () => {},
} as const;

const validatedDefaultProps: ReactDefaultPropsWithChildren<
  AccordionItemBaseProps,
  typeof defaultProps
> = defaultProps;

const AccordionItemWithRef = (
  props: AccordionItemProps,
  ref: ComponentPropsWithRef<"div">["ref"]
) => {
  const {
    children,
    isExpanded: expandedState = defaultProps.isExpanded,
    itemId = defaultProps.itemId,
    customClass = defaultProps.customClass,
    onToggle = defaultProps.onToggle,
    ...restProps
  } = props;
  const [isExpanded, setIsExpanded] = useState(expandedState);
  const accordion = useContext(AccordionContext) || {};
  const id = itemId || `${accordion.id}-${accordion.itemId}`;

  const toggle = () => {
    onToggle(!isExpanded);
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    if (expandedState !== isExpanded) {
      setIsExpanded(expandedState);
    }
  }, [expandedState]);

  return (
    <AccordionItemProvider value={{ isExpanded, toggle, id }}>
      <div ref={ref} className={cn(styles.item, customClass)} {...restProps}>
        {children}
      </div>
    </AccordionItemProvider>
  );
};

export const AccordionItem = Object.assign(forwardRef(AccordionItemWithRef), {
  displayName: "Accordion.Item",
  propTypes,
  defaultProps: validatedDefaultProps,
  Toggle: AccordionToggle,
  Headline: AccordionHeadline,
  Subline: AccordionSubline,
  Panel: AccordionPanel,
});

export default AccordionItem;
